import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { Pagination } from '@/common/types';
import { API_URL, IFileErrorDto } from '@/dtos';

import { FileError, FileErrorDto, FileErrorTypes } from '../types/fileReport.constants';

export const QUERY_KEY = 'useFileErrors';

export interface FileErrorsParams {
  fileId: string;
  page?: number;
  perPage?: number;
  sort?: string;
  enabled?: boolean;
}

export interface ResponseData {
  content: FileErrorDto[];
  number: number;
  numberOfElements: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const formatData = (data: IFileErrorDto[]): FileError[] =>
  data.map((fileError: IFileErrorDto) => ({
    id: toString(fileError.id) || '',
    fileId: toString(fileError.file_id) || '',
    errorDetails: fileError.error_details || '',
    fileErrorType: fileError.file_error_type as unknown as FileErrorTypes,
    createdAt: fileError.created_at as unknown as string,
  }));

const useFileErrorsQuery = ({ fileId, page, perPage, sort, enabled }: FileErrorsParams) => {
  const params = useMemo(
    () => ({
      page,
      size: perPage,
      sort,
    }),
    [page, perPage, sort]
  );

  const { data, isLoading, isSuccess, isError } = useQuery(
    [QUERY_KEY, page, perPage, fileId],
    () => api.get<Pagination<IFileErrorDto>>(API_URL.FILE_FILES_ERRORS_BY_FILE_ID(fileId), { params }),
    { enabled }
  );

  const total = useMemo<number>(() => data?.data?.totalElements || 0, [data]);
  const fileErrorType = useMemo(
    () => (data?.data?.content?.[0]?.file_error_type || undefined) as unknown as FileErrorTypes | undefined,
    [data]
  );
  const fileErrorDescription = useMemo(() => data?.data?.content?.[0]?.error_details || undefined, [data]);

  return {
    formattedData: formatData(data?.data.content || []),
    data: (data?.data?.content as unknown as FileErrorDto[]) || [],
    fileErrorType,

    fileErrorDescription,
    total,
    isError,
    isLoading,
    isSuccess,
  };
};

export default useFileErrorsQuery;
